import { Link } from "react-router"
import type { SponsorshipRequest, SponsorPortal } from "../sponsor-session-context"

const STATUS_COPY: Record<SponsorshipRequest["status"], { label: string; description: string; className: string }> = {
  submitted: {
    label: "Request received",
    description: "Our team has received your sponsorship request and will review it shortly.",
    className: "bg-amber-100 text-amber-900",
  },
  reviewing: {
    label: "Conversation started",
    description: "Our team has reviewed your request and is in touch about the next steps.",
    className: "bg-blue-100 text-blue-900",
  },
  approved: {
    label: "Sponsorship approved",
    description: "Your sponsorship request has been approved. Our team will guide you through matching and funding.",
    className: "bg-emerald-100 text-emerald-900",
  },
  declined: {
    label: "Request closed",
    description: "This sponsorship request is currently closed. Contact Redempta if you would like to discuss it.",
    className: "bg-rose-100 text-rose-900",
  },
  closed: {
    label: "Request closed",
    description: "This sponsorship request is closed. Contact Redempta if you would like to discuss it.",
    className: "bg-slate-100 text-slate-900",
  },
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
}

export function SponsorshipStatusCard({
  request,
  matchState,
  activeMatchCount,
  full = false,
}: {
  request: SponsorshipRequest
  matchState: SponsorPortal["matchState"]
  activeMatchCount: number
  full?: boolean
}) {
  const requestCopy = STATUS_COPY[request.status]
  const copy = matchState === "matched"
    ? {
        label: "Sponsorship active",
        description: `Your sponsorship is active with ${activeMatchCount} matched ${activeMatchCount === 1 ? "student" : "students"}.`,
        className: "bg-emerald-100 text-emerald-900",
      }
    : matchState === "rematch_needed"
      ? {
          label: "Awaiting a new match",
          description: "Your previous match has ended. Your sponsorship remains approved while we find a new student match.",
          className: "bg-amber-100 text-amber-900",
        }
      : matchState === "awaiting_match"
        ? {
            label: "Approved - awaiting match",
            description: "Your sponsorship request is approved. Our team is working on the right student match.",
            className: "bg-emerald-100 text-emerald-900",
          }
        : requestCopy

  return (
    <section className="rounded-2xl border border-gold-500 bg-white p-6 shadow-sm sm:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-forest-700">Sponsorship request</p>
          <h2 className="mt-2 font-display text-2xl font-bold text-ink">{copy.label}</h2>
        </div>
        <span className={`rounded-full px-3 py-1.5 text-xs font-semibold ${copy.className}`}>{copy.label}</span>
      </div>
      <p className="mt-4 max-w-2xl text-sm leading-6 text-ink/65">{copy.description}</p>
      <dl className="mt-6 grid gap-4 border-t border-ink/10 pt-5 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-ink/50">Submitted</dt>
          <dd className="mt-1 font-medium text-ink">{formatDate(request.createdAt)}</dd>
        </div>
        <div>
          <dt className="text-ink/50">Last updated</dt>
          <dd className="mt-1 font-medium text-ink">{formatDate(request.updatedAt)}</dd>
        </div>
      </dl>
      {full && (
        <div className="mt-6 border-t border-ink/10 pt-5">
          <h3 className="text-sm font-semibold text-ink">Your request</h3>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-ink/65">{request.message}</p>
        </div>
      )}
      {!full && (
        <Link to="/donor/request" className="mt-6 inline-flex rounded-full bg-forest-950 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-forest-800">
          View sponsorship request
        </Link>
      )}
    </section>
  )
}
